/**
 * Object generator system — parametric recipes for building SceneObjects
 * whose geometry can be rebuilt from a small set of user-facing parameters.
 *
 * A generator is a first-class, persistent description: it survives save /
 * reload, shows up in the sidebar as an editable section, and remains
 * regenerable until the first destructive edit-mode topology op bakes it.
 *
 * Two shapes:
 *  - `{ kind: "mesh" }`: single SceneObject carrying geometry.
 *  - `{ kind: "group" }`: parent transform container with a list of child
 *    SceneObjects. Each child has a stable `roleId` so `rebuild()` can
 *    reuse the same child SceneObject id across regeneration — preserving
 *    selection, materials, and per-object properties that don't live under
 *    generator control.
 *
 * Param schema — a discriminated union of primitive types (number, boolean,
 * enum) plus a recursive `list` type whose items are themselves typed by a
 * sub-schema selected from a fixed set of `GeneratorListItemType`s. List
 * items are how a generator exposes "conditional" parameters (e.g. a Room's
 * doorways/windows), and the nesting is handled uniformly by the sidebar
 * UI, the codec, and the rebuild pipeline.
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import type { PrimitiveGeometry } from "../primitives.ts";
import type { ObjectKind, SceneObject } from "../types.ts";

// ---------------------------------------------------------------------------
// Param specs — discriminated union
// ---------------------------------------------------------------------------

export type GeneratorParamUnit = "count" | "meters" | "degrees" | "radians";

export interface GeneratorEnumOption {
  value: string;
  label: string;
}

/**
 * Conditional visibility condition for a param. A condition passes only when
 * the sibling param identified by `id` equals `value` — or, when `value` is an
 * array, equals any of its entries. When `negate` is true, the sense of the
 * match is flipped. Both params must live at the same level — either both
 * top-level generator params, or both sub-params of the same list item type.
 */
export interface GeneratorParamCondition {
  /** `id` of a sibling param in the same `paramSpecs` array. */
  id: string;
  /**
   * Required value (or set of values) for the condition to pass before
   * optional negation. A bare value matches by strict equality; an array
   * matches if the sibling's current value equals any entry. Using an array
   * is the idiomatic way to show a sub-param for multiple enum variants
   * (e.g. a strand count visible when `style` is `"cable"` *or* `"bundle"`).
   */
  value: boolean | number | string | readonly (boolean | number | string)[];
  /**
   * When true, the condition passes when the param does **not** match `value`.
   * Useful for hiding a sub-param when an enum is set to its "off" sentinel
   * (e.g. `showIf: { id: "paneStyle", value: "none", negate: true }`).
   */
  negate?: boolean;
}

export type GeneratorParamVisibility =
  | GeneratorParamCondition
  | readonly GeneratorParamCondition[];

interface GeneratorParamSpecBase {
  /** Stable param identifier — used as key into `GeneratorParams`. */
  id: string;
  /** User-facing label. */
  label: string;
  /** Optional longer description. */
  description?: string;
  /**
   * When set, this param is only rendered by the sidebar when every condition
   * passes. The underlying value is still stored and participates in geometry
   * building regardless of visibility.
   */
  showIf?: GeneratorParamVisibility;
  /**
   * Stored and decoded normally, but omitted from the sidebar editor.
   * Use for generator source data captured by a tool (for example a surface
   * cutter footprint) that must persist and rebuild, but should not be
   * exposed as a raw form control.
   */
  hidden?: boolean;
}

export interface GeneratorNumberParamSpec extends GeneratorParamSpecBase {
  type: "number";
  unit?: GeneratorParamUnit;
  default: number;
  min?: number;
  max?: number;
  step?: number;
  /**
   * Optional dynamic slider range resolved from sibling params at render time.
   * The stored value remains in the generator's normal units; this only lets
   * controls scale their min/max/default/step to captured source geometry.
   */
  resolveRange?: (
    context: GeneratorParams,
  ) => Partial<
    Pick<GeneratorNumberParamSpec, "default" | "min" | "max" | "step">
  >;
}

export interface GeneratorBooleanParamSpec extends GeneratorParamSpecBase {
  type: "boolean";
  default: boolean;
}

export interface GeneratorEnumParamSpec extends GeneratorParamSpecBase {
  type: "enum";
  default: string;
  options: readonly GeneratorEnumOption[];
  /**
   * Renderer hint. `"select"` (default) draws a `<select>` dropdown — the
   * idiomatic affordance for ≥ 3 options. `"segmented"` draws a
   * SegmentedControl strip — better for two- or three-way mutually
   * exclusive choices where the options are short and can sit visible
   * side by side. Purely presentational; the stored value type and the
   * params record are unchanged.
   */
  display?: "select" | "segmented";
}

/**
 * String parameter — free-form text input. The `display` hint chooses
 * between a single-line `<input>` and a multi-line `<textarea>`. Used by
 * generators whose geometry is driven by user-typed copy (e.g. the text
 * generator); not appropriate for bounded choices, which should use
 * `enum`.
 */
export interface GeneratorStringParamSpec extends GeneratorParamSpecBase {
  type: "string";
  default: string;
  /** `"single"` (default) → `<input>`, `"multiline"` → `<textarea>`. */
  display?: "single" | "multiline";
  /** Optional placeholder shown when the value is empty. */
  placeholder?: string;
  /** Sanity ceiling on character count, enforced at the input layer. */
  maxLength?: number;
}

/**
 * Object-reference parameter — stores the id of another SceneObject the
 * generator depends on. The generator's `build` / `rebuild` resolve the
 * reference through the `GeneratorBuildContext` to read the referenced
 * object's transform (or any other state). `null` means "unset".
 *
 * The store watches scene-object transform updates and, whenever a
 * generator's referenced object moves, re-runs rebuild for that generator
 * so the dependent geometry stays live.
 */
export interface GeneratorObjectRefParamSpec extends GeneratorParamSpecBase {
  type: "objectRef";
  /**
   * Optional filter narrowing the picker to specific ObjectKinds. When
   * omitted, every scene object (including empties) is eligible.
   */
  objectKinds?: readonly ObjectKind[];
  default: string | null;
}

/**
 * List parameter — a user-editable sequence of typed items whose sub-schemas
 * are drawn from a fixed `itemTypes` set. Each item carries its own nested
 * `GeneratorParams`, giving generators a natural way to expose "conditional"
 * parameters (different schemas per subtype) without ad-hoc escape hatches.
 */
export interface GeneratorListParamSpec extends GeneratorParamSpecBase {
  type: "list";
  /** Subtypes the user can add to this list. First entry is the default. */
  itemTypes: readonly GeneratorListItemType[];
  /** Default (usually empty). */
  default: readonly GeneratorListItem[];
}

/**
 * Section param — a purely structural grouping of sibling params under a
 * collapsible header. Does not store a value; its `params` children are
 * still top-level entries in the owner's `GeneratorParams` record.
 *
 * Used schema-side to declare collapsible groups within a generator's param
 * list without encoding presentation logic into the generator itself.
 * FoldoutSection (in shared.tsx) is the component-side counterpart for
 * one-off groupings that don't belong in the schema.
 */
export interface GeneratorSectionParamSpec extends GeneratorParamSpecBase {
  type: "section";
  /** Whether the section starts collapsed on first visit. */
  defaultCollapsed?: boolean;
  /** Child param specs grouped under this section header. */
  params: readonly GeneratorParamSpec[];
}

export type GeneratorParamSpec =
  | GeneratorNumberParamSpec
  | GeneratorBooleanParamSpec
  | GeneratorEnumParamSpec
  | GeneratorStringParamSpec
  | GeneratorObjectRefParamSpec
  | GeneratorListParamSpec
  | GeneratorSectionParamSpec;

// ---------------------------------------------------------------------------
// List items — a recursive sub-schema per subtype
// ---------------------------------------------------------------------------

/**
 * One subtype available inside a `GeneratorListParamSpec`. An item type is
 * its own mini-generator schema: it carries its own `paramSpecs` and its
 * own `defaultParams`, and is identified within a list by `id`.
 */
export interface GeneratorListItemType {
  /** Stable subtype id (e.g. "doorway", "window"). */
  id: string;
  /** User-facing label. */
  label: string;
  /** Optional icon registry key. */
  icon?: string;
  /** Sub-schema for items of this subtype. */
  paramSpecs: readonly GeneratorParamSpec[];
  /** Default params for a freshly added item. */
  defaultParams: GeneratorParams;
}

/**
 * One item stored inside a list parameter. `itemType` names the subtype
 * and `params` is validated against that subtype's `paramSpecs`.
 */
export interface GeneratorListItem {
  /** Stable identifier unique within the enclosing list. */
  id: string;
  /** Which subtype — must match one of the list's `itemTypes.id`. */
  itemType: string;
  /** Nested params; validated by the subtype's sub-schema. */
  params: GeneratorParams;
}

// ---------------------------------------------------------------------------
// Params — the untyped record passed between the sidebar, store, and builders
// ---------------------------------------------------------------------------

export type GeneratorParamValue =
  | number
  | boolean
  | string
  | null
  | readonly GeneratorListItem[];

/**
 * Raw parameter record shared by the sidebar, store, and builders. The
 * schema (what keys exist, what values they may take) is described by
 * `GeneratorParamSpec[]`; builders coerce through the typed readers
 * (`numParam` / `boolParam` / `enumParam` / `listParam`) so invalid input
 * always falls back to a default rather than corrupting a build.
 */
export type GeneratorParams = { readonly [key: string]: GeneratorParamValue };

// ---------------------------------------------------------------------------
// Coercion readers — the fail-fast boundary between the sidebar's untyped
// record and a generator's strongly-typed params. Each reader validates a
// single key, falls back to a default on malformed input, and is the only
// legitimate way for a generator body to pull from a raw `GeneratorParams`.
// ---------------------------------------------------------------------------

export function numParam(
  p: GeneratorParams,
  id: string,
  fallback: number,
): number {
  const v = p[id];
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

export function intParam(
  p: GeneratorParams,
  id: string,
  fallback: number,
): number {
  return Math.round(numParam(p, id, fallback));
}

export function boolParam(
  p: GeneratorParams,
  id: string,
  fallback: boolean,
): boolean {
  const v = p[id];
  return typeof v === "boolean" ? v : fallback;
}

export function enumParam(
  p: GeneratorParams,
  id: string,
  fallback: string,
): string {
  const v = p[id];
  return typeof v === "string" ? v : fallback;
}

export function stringParam(
  p: GeneratorParams,
  id: string,
  fallback: string,
): string {
  const v = p[id];
  return typeof v === "string" ? v : fallback;
}

export function listParam(
  p: GeneratorParams,
  id: string,
): readonly GeneratorListItem[] {
  const v = p[id];
  return Array.isArray(v) ? (v as readonly GeneratorListItem[]) : [];
}

/**
 * Object-reference reader. Returns the stored SceneObject id, or `null` when
 * the param is unset or malformed. Generators should resolve the id through
 * `GeneratorBuildContext.resolveObject` rather than assuming it still points
 * at a live object — referenced objects can be deleted between builds.
 */
export function objectRefParam(p: GeneratorParams, id: string): string | null {
  const v = p[id];
  return typeof v === "string" && v.length > 0 ? v : null;
}

/** Build a default-params record from a spec list. Recurses into sections. */
export function paramsFromSpecs(
  specs: readonly GeneratorParamSpec[],
): GeneratorParams {
  const out: { [key: string]: GeneratorParamValue } = {};
  for (const spec of specs) {
    if (spec.type === "section") {
      Object.assign(out, paramsFromSpecs(spec.params));
    } else if (spec.type === "list") {
      out[spec.id] = [...spec.default];
    } else {
      out[spec.id] = spec.default;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// List item id allocation
// ---------------------------------------------------------------------------

let _nextListItemId = 1;

/** Generate a unique id for a new `GeneratorListItem`. */
export function createListItemId(): string {
  return `gli_${_nextListItemId++}_${Date.now().toString(36)}`;
}

// ---------------------------------------------------------------------------
// Generator output — what a build / rebuild call produces
// ---------------------------------------------------------------------------

/** Local transform of a generator child relative to its group parent. */
export interface GeneratorLocalTransform {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: [number, number, number];
}

export const IDENTITY_LOCAL_TRANSFORM: GeneratorLocalTransform = {
  position: [0, 0, 0],
  rotation: [0, 0, 0],
  scale: [1, 1, 1],
};

export interface GeneratorGroupParent {
  name: string;
}

export interface GeneratorChild {
  /** Stable per-child identifier — matches children across rebuilds. */
  roleId: string;
  /** Display name. */
  name: string;
  /** ObjectKind to stamp on the SceneObject (mesh-shaped primitives use "cube", etc.). */
  objectKind: ObjectKind;
  /** Geometry for this child, in child-local space. */
  geometry: PrimitiveGeometry;
  /** Transform offset relative to the parent's local origin. */
  localTransform: GeneratorLocalTransform;
}

export type GeneratorOutput =
  | {
      kind: "mesh";
      geometry: PrimitiveGeometry;
      /**
       * When `true`, the generator emitted geometry in world space and the
       * store must spawn / keep the owning SceneObject at the world origin
       * (identity transform). Used by generators like `cable` whose geometry
       * is anchored to other objects' world positions rather than to the
       * owning object's transform. Default `false`.
       */
      worldSpace?: boolean;
      /**
       * Modifiers the generator wants attached to its output. Store installs
       * these as `ModifierOverride`s tagged `generatorOwned: true` on the
       * produced SceneObject, and re-installs them on every rebuild. A
       * contiguous block of generator-owned overrides is replaced as a unit;
       * user-added overrides around it are preserved. Lets a generator like
       * `cable` ship a Skin modifier that produces its tube visual at render
       * time while the underlying mesh remains a wire polyline (simulatable
       * by `settleParticles` without the face-pinning constraint kicking in).
       */
      generatorModifiers?: readonly BlendModifier[];
    }
  | { kind: "group"; parent: GeneratorGroupParent; children: GeneratorChild[] };

// ---------------------------------------------------------------------------
// Build context — scene access for generators
// ---------------------------------------------------------------------------

/**
 * Scene-access surface passed to every `build` / `rebuild` call. Threaded
 * from the store so generators can resolve `objectRef` params without
 * pulling the store into the generator layer (which would create a cycle).
 *
 * Keep this interface minimal — each added method expands the contract
 * every generator implementation must honor, and bloats the store-side
 * shim. Add new accessors only when a generator genuinely needs them.
 */
export interface GeneratorBuildContext {
  /**
   * Resolve a SceneObject by id. Returns `null` when the id is null/empty
   * or when no object with that id currently exists — generators must
   * handle the null case gracefully (e.g. fall back to a placeholder shape)
   * because referenced objects can be deleted between builds.
   */
  resolveObject(id: string | null): SceneObject | null;
}

// ---------------------------------------------------------------------------
// Generator definition
// ---------------------------------------------------------------------------

export type GeneratorCategory = "primitive" | "environment" | "prop";

/**
 * A helper SceneObject a generator wants spawned alongside its own output
 * on `addGenerator`. The store creates a plain sibling SceneObject of
 * `objectKind` at `cursor + offset`, then writes its freshly allocated id
 * into the generator's params under `bindTo` so the generator's own build
 * picks it up as an `objectRef`.
 *
 * Used by generators that are *defined by* other scene objects — e.g. the
 * cable, which exists between two empties and would otherwise force every
 * new user to create those empties manually and wire them up through the
 * sidebar. By declaring them as companions, a single toolbar click spawns
 * the whole rig (cable + endpoints) already wired and ready to drag.
 *
 * Companions become ordinary, independently selectable SceneObjects after
 * creation — they are not children or a collection. Deleting a companion
 * gracefully falls the dependent generator back to its placeholder shape
 * via the same `resolveObject() → null` path a user-cleared ref would.
 */
export interface GeneratorCompanion {
  /** Stable identifier used for debugging / future introspection. */
  id: string;
  /**
   * ObjectKind of the spawned SceneObject. Only "empty" is exercised so
   * far, but the field is general — a future generator could seed a
   * mesh companion if that ever makes sense.
   */
  objectKind: ObjectKind;
  /** Base display name (suffix-numbered in-scene to avoid collisions). */
  name: string;
  /** Position offset from the 3D cursor at spawn time, in meters. */
  offset: [number, number, number];
  /**
   * Id of the generator param (objectRef) that receives this companion's
   * freshly allocated SceneObject id. Must point at an `objectRef` spec
   * in the generator's `paramSpecs`.
   */
  bindTo: string;
}

/**
 * Declaration for a compound generator child the user may hide without
 * regenerating the room. The child's SceneObject stays in the scene
 * graph; only the global `visibility` map is flipped. This keeps any
 * user-applied material, transform, and selection state intact across
 * show/hide cycles — unlike the `has*` boolean params (e.g. `hasCeiling`),
 * which are topology-level toggles that destroy and recreate the member.
 *
 * `roleId` must match a `roleId` emitted by the generator's `build()` so
 * the panel can resolve the live SceneObject id via the collection's
 * `generatorRef.roleToObjectId` map.
 */
export interface GeneratorHideablePart {
  roleId: string;
  label: string;
  /**
   * Optional: additional child roles that must toggle together with this
   * part — e.g. the crown molding, baseboard, and any doorways/windows
   * attached to a wall. Resolved against the generator's current params
   * so dynamically-named roles (list-item children like
   * `door_panel_<id>`) can participate. Roles not currently in the
   * collection's `roleToObjectId` are skipped silently, so implementations
   * can return a superset (e.g. all possible detail sub-roles regardless
   * of `hasPanel`/`hasFrame` toggles).
   *
   * `roleId` itself must NOT be included.
   */
  dependentRoles?: (params: GeneratorParams) => readonly string[];
}

/**
 * Public, type-erased shape stored in the registry. Homogeneous so all
 * generators can live in the same `Record<string, Generator>` without
 * variance pain. Each entry is produced via `defineGenerator` (below),
 * which keeps the authoring site strongly typed over the generator's
 * own param interface.
 */
export interface Generator {
  /** Stable registry id ("cube", "sphere", "room", ...). */
  id: string;
  /** User-facing label. */
  label: string;
  category: GeneratorCategory;
  /** Optional icon registry key. */
  icon?: string;
  paramSpecs: readonly GeneratorParamSpec[];
  defaultParams: GeneratorParams;
  /**
   * Children whose SceneObjects users may toggle non-destructively via
   * the global visibility map. Compound generators only — single-mesh
   * outputs use the hierarchy panel's per-object eye toggle directly.
   */
  hideableParts?: readonly GeneratorHideablePart[];
  /**
   * Sibling SceneObjects to spawn alongside this generator's own output
   * on `addGenerator`. See `GeneratorCompanion`. Runs only on add — never
   * on rebuild — so existing scenes are never retroactively populated.
   */
  companions?: readonly GeneratorCompanion[];
  /**
   * Ids of operators (from the `OPERATORS` registry in
   * `modeling/operators.ts`) the generator wants exposed as one-click
   * buttons in its sidebar panel. Rendered below the param rows in the
   * order given, each honouring the operator's own `isEnabled(ctx)`
   * predicate against the current store state.
   *
   * This is the sanctioned way for a generator to surface a mode-
   * specific workflow step — e.g. a cable exposing `simulate_gravity`
   * so the user can drape it without hunting for it in the operator
   * search palette. Generators reference operators by string id rather
   * than binding a closure so the two layers stay decoupled: a
   * generator doesn't take a compile-time dependency on the operators
   * module, and the store-touching parts of an operator stay in one
   * place.
   */
  operatorIds?: readonly string[];
  /** Build a fresh output from params. */
  build(params: GeneratorParams, ctx: GeneratorBuildContext): GeneratorOutput;
  /**
   * Rebuild an output from params. The default implementation simply
   * delegates to `build`; the separate method exists so future compound
   * generators that need the previous output (e.g. to seed a deterministic
   * random stream) have a hook — role-based child matching is handled by
   * the store, not the generator.
   */
  rebuild(
    params: GeneratorParams,
    ctx: GeneratorBuildContext,
    prev?: GeneratorOutput,
  ): GeneratorOutput;
  /**
   * Identity token for the inputs that actually affect produced *geometry*
   * (vertex positions, topology, loop normals). Style / appearance inputs
   * that only influence generator-owned modifiers must be excluded.
   *
   * When provided, the store skips the vertex/topology write on a rebuild
   * whose signature matches the previously-stored one, keeping user-mutated
   * positions (e.g. a gravity-draped cable) intact across style edits.
   * Any change that should reset positions — endpoint movement, vertex
   * count change, structural param — must flip the signature.
   *
   * Must be pure w.r.t. its inputs and cheap to compute; called on every
   * param-adjust frame. Single-mesh generators only for now (compound
   * rebuild goes through per-child patching and isn't plumbed through).
   */
  geometrySignature?(
    params: GeneratorParams,
    ctx: GeneratorBuildContext,
  ): string;
}

/**
 * Authoring-time config for `defineGenerator`. The generic `TParams` is
 * the generator's own param interface — it must carry the
 * `GeneratorParams` index signature so TypeScript can verify that the
 * sidebar's untyped record is a structural supertype at the boundary.
 */
export interface GeneratorDefinition<TParams extends GeneratorParams> {
  id: string;
  label: string;
  category: GeneratorCategory;
  icon?: string;
  paramSpecs: readonly GeneratorParamSpec[];
  defaultParams: TParams;
  hideableParts?: readonly GeneratorHideablePart[];
  companions?: readonly GeneratorCompanion[];
  operatorIds?: readonly string[];
  build(params: TParams, ctx: GeneratorBuildContext): GeneratorOutput;
  rebuild?(
    params: TParams,
    ctx: GeneratorBuildContext,
    prev?: GeneratorOutput,
  ): GeneratorOutput;
  /** See `Generator.geometrySignature`. */
  geometrySignature?(params: TParams, ctx: GeneratorBuildContext): string;
}

/**
 * Produce a type-erased `Generator` from a strongly-typed definition.
 * The authored `build` / `rebuild` receive `TParams`, which gives
 * compile-time key checking at the define site. The returned `Generator`
 * accepts the runtime-generic `GeneratorParams` and defers validation to
 * the coercion layer above.
 *
 * Using a factory rather than a constraint on `Generator<TParams>` keeps
 * the registry invariance-free and lets every entry live in the same
 * homogeneous map.
 */
export function defineGenerator<TParams extends GeneratorParams>(
  def: GeneratorDefinition<TParams>,
): Generator {
  const build = (
    p: GeneratorParams,
    ctx: GeneratorBuildContext,
  ): GeneratorOutput => def.build(p as TParams, ctx);
  const rebuild = def.rebuild
    ? (
        p: GeneratorParams,
        ctx: GeneratorBuildContext,
        prev?: GeneratorOutput,
      ): GeneratorOutput => def.rebuild!(p as TParams, ctx, prev)
    : build;
  const geometrySignature = def.geometrySignature
    ? (p: GeneratorParams, ctx: GeneratorBuildContext): string =>
        def.geometrySignature!(p as TParams, ctx)
    : undefined;
  return {
    id: def.id,
    label: def.label,
    category: def.category,
    icon: def.icon,
    paramSpecs: def.paramSpecs,
    defaultParams: def.defaultParams,
    hideableParts: def.hideableParts,
    companions: def.companions,
    operatorIds: def.operatorIds,
    build,
    rebuild,
    geometrySignature,
  };
}

// ---------------------------------------------------------------------------
// Generator refs
// ---------------------------------------------------------------------------

/**
 * Reference on a SceneObject to the single-mesh generator that produced it.
 * Carries just id + params; the object *is* the generator's output.
 *
 * Compound generators use `CollectionGeneratorRef` on the owning Collection
 * instead (the collection holds the authoritative params and the role map,
 * its members are the generated children).
 *
 * Absence of `generator` on a SceneObject means "fully baked, behaves
 * exactly like a hand-authored object".
 */
export interface GeneratorRef {
  /** Key into the registry. */
  generatorId: string;
  /** Current parameter values. */
  params: GeneratorParams;
  /**
   * Mirrors the latest build output's `worldSpace` flag — `true` when the
   * generator emits geometry in world space and the owning SceneObject
   * should be pinned to the origin / identity transform. The store reads
   * this on transform-update paths and drops drag attempts on the owner
   * so the user can't desync the visual with its endpoint-anchored
   * geometry. Stored on the ref (rather than recomputed) so the check
   * doesn't have to re-run the generator for every frame of every drag.
   */
  worldSpace?: boolean;
  /**
   * Identity token from the last rebuild that actually wrote geometry. The
   * store compares this to the token produced for a pending rebuild's
   * inputs; if they match, vertices / topology / loop normals are left
   * untouched and only the generator's appearance modifiers + params are
   * refreshed. Produced by `Generator.geometrySignature` — absent when the
   * generator doesn't declare one (falls back to full rebuild every time,
   * matching the pre-hook behaviour).
   */
  geometrySignature?: string;
}

/**
 * Reference on a Collection to the compound generator that produced it.
 *
 * `roleToObjectId` maps each stable child `roleId` (from the generator's
 * build output) to the SceneObject id that was allocated for it. Rebuild
 * uses this map to patch existing children in place instead of recreating
 * them — preserving selection, materials, and every per-object property
 * the generator doesn't control.
 */
export interface CollectionGeneratorRef {
  /** Key into the registry. */
  generatorId: string;
  /** Current parameter values — authoritative for the whole group. */
  params: GeneratorParams;
  /** Stable role id → child SceneObject id. */
  roleToObjectId: { [role: string]: string };
}
